'use client';

import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react';
import {
  type ComponentPropsWithoutRef,
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useState
} from 'react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface SearchableSelectContextType {
  items: Map<string, ReactNode>;
  onItemAdded: (value: string, label: ReactNode) => void;
  open: boolean;
  selectedValue: null | string;
  selectValue: (value: string) => void;
  setOpen: (open: boolean) => void;
}
const SearchableSelectContext =
  createContext<null | SearchableSelectContextType>(null);

export function SearchableSelect({
  children,
  defaultValue,
  onValueChange,
  value
}: {
  children: ReactNode;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  value?: string;
}) {
  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState<null | string>(
    value ?? defaultValue ?? null
  );
  const selectedValue = value ?? internalValue;
  const [items, setItems] = useState<Map<string, ReactNode>>(() => new Map());

  function selectValue(newValue: string) {
    setInternalValue(newValue);
    onValueChange?.(newValue);
    setOpen(false);
  }

  const onItemAdded = useCallback((value: string, label: ReactNode) => {
    setItems(prev => {
      if (prev.get(value) === label) return prev;
      return new Map(prev).set(value, label);
    });
  }, []);

  return (
    <SearchableSelectContext
      value={{
        items,
        onItemAdded,
        open,
        selectedValue,
        selectValue,
        setOpen
      }}
    >
      <Popover
        modal={true}
        onOpenChange={setOpen}
        open={open}
      >
        {children}
      </Popover>
    </SearchableSelectContext>
  );
}

export function SearchableSelectContent({
  children,
  search = true,
  ...props
}: {
  children: ReactNode;
  search?: { emptyMessage?: string; placeholder?: string } | boolean;
} & Omit<ComponentPropsWithoutRef<typeof Command>, 'children'>) {
  const canSearch = typeof search === 'object' ? true : search;

  return (
    <>
      <div style={{ display: 'none' }}>
        <Command>
          <CommandList>{children}</CommandList>
        </Command>
      </div>
      <PopoverContent className="min-w-[var(--radix-popover-trigger-width)] p-0">
        <Command {...props}>
          {canSearch ? (
            <CommandInput
              placeholder={
                typeof search === 'object'
                  ? (search.placeholder ?? 'Search...')
                  : 'Search...'
              }
            />
          ) : (
            <button
              autoFocus
              className="sr-only"
            />
          )}
          <CommandList>
            {canSearch && (
              <CommandEmpty>
                {typeof search === 'object'
                  ? (search.emptyMessage ?? 'No results found.')
                  : 'No results found.'}
              </CommandEmpty>
            )}
            {children}
          </CommandList>
        </Command>
      </PopoverContent>
    </>
  );
}

export function SearchableSelectGroup(
  props: ComponentPropsWithoutRef<typeof CommandGroup>
) {
  return <CommandGroup {...props} />;
}

export function SearchableSelectItem({
  children,
  onSelect,
  value,
  ...props
}: {
  value: string;
} & Omit<ComponentPropsWithoutRef<typeof CommandItem>, 'value'>) {
  const { onItemAdded, selectedValue, selectValue } =
    useSearchableSelectContext();
  const isSelected = selectedValue === value;

  useEffect(() => {
    onItemAdded(value, children);
  }, [value, children, onItemAdded]);

  return (
    <CommandItem
      {...props}
      onSelect={() => {
        selectValue(value);
        onSelect?.(value);
      }}
    >
      <CheckIcon
        className={cn('mr-2 size-4', isSelected ? 'opacity-100' : 'opacity-0')}
      />
      {children}
    </CommandItem>
  );
}

export function SearchableSelectSeparator(
  props: ComponentPropsWithoutRef<typeof CommandSeparator>
) {
  return <CommandSeparator {...props} />;
}

export function SearchableSelectTrigger({
  children,
  className,
  ...props
}: {
  children?: ReactNode;
  className?: string;
} & ComponentPropsWithoutRef<typeof Button>) {
  const { open } = useSearchableSelectContext();

  return (
    <PopoverTrigger asChild>
      <Button
        {...props}
        aria-expanded={props['aria-expanded'] ?? open}
        className={cn(
          "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex h-9 w-fit items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
          className
        )}
        role={props.role ?? 'combobox'}
        variant={props.variant ?? 'outline'}
      >
        {children}
        <ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
      </Button>
    </PopoverTrigger>
  );
}

export function SearchableSelectValue({
  className,
  placeholder,
  ...props
}: {
  placeholder?: string;
} & Omit<ComponentPropsWithoutRef<'span'>, 'children'>) {
  const { items, selectedValue } = useSearchableSelectContext();

  if (!selectedValue || !items.has(selectedValue)) {
    return (
      <span
        className={cn(
          'text-muted-foreground min-w-0 overflow-hidden font-normal',
          className
        )}
        data-placeholder
        {...props}
      >
        {placeholder ?? 'Select...'}
      </span>
    );
  }

  return (
    <span
      className={cn('min-w-0 overflow-hidden', className)}
      {...props}
    >
      {items.get(selectedValue)}
    </span>
  );
}

function useSearchableSelectContext() {
  const context = use(SearchableSelectContext);
  if (context == null) {
    throw new Error(
      'useSearchableSelectContext must be used within a SearchableSelectContext'
    );
  }
  return context;
}
